import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { EmptyState } from '../empty-state/empty-state.tsx';
import { Spinner } from '../spinner/spinner.tsx';
import { TableHead } from './table-head.tsx';
import { TablePagination } from './table-pagination.tsx';
import { TableRow } from './table-row.tsx';
import { TableScrollBox } from './table-scroll-box.tsx';
import { TableSkeletonRows } from './table-skeleton-rows.tsx';
import { TableToolbar } from './table-toolbar.tsx';
import type {
  DataTableColumn,
  DataTablePagination,
  DataTableSearch,
  DataTableSort,
} from './types.ts';

export type DataTableMinWidth = 'md' | 'lg';

const minWidthClass: Record<DataTableMinWidth, string> = {
  md: 'min-w-mobile',
  lg: 'min-w-agenda',
};

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  /** Stable identity per row — never the array index. */
  rowKey: (row: T) => string;
  /** What this table lists, for a screen reader reaching it out of context. */
  label?: string;

  /** Controlled. The component draws the indicator and calls back; it never reorders rows. */
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;

  /** Controlled. Server-side or client-side paging is the caller's business. */
  pagination?: DataTablePagination;
  onPageChange?: (page: number) => void;

  /** Renders the field; debounces by 250ms. Filtering is the caller's business. */
  search?: DataTableSearch;
  /** Filters and the primary action, beside the search field. */
  toolbar?: ReactNode;

  /**
   * Whole-row navigation. The first cell becomes a real link, and clicking
   * anywhere else on the row follows it. One target, one tab stop, and the row
   * can be opened in a new tab like any other link.
   *
   * `undefined` for a row that has nowhere to go — a draft with no page yet, a
   * record the reader may not open. That row keeps its plain first cell and
   * falls through to `onRowClick`, if there is one.
   */
  rowHref?: (row: T) => string | undefined;
  /**
   * Where a plain left click on a `rowHref` row should be handed instead of the
   * browser — a client-side router. The link keeps its real `href`, so
   * middle-click, cmd/ctrl-click and "open in a new tab" still do what they
   * always do; only the one click that would have reloaded the whole app is
   * intercepted. Without it the link is followed normally.
   */
  onNavigate?: (href: string) => void;
  /**
   * For a row that opens something that is not a URL — a side peek. Ignored
   * when `rowHref` is set: navigation is a link's job. Such a row is a tab
   * stop of its own, opened with Enter or Space.
   */
  onRowClick?: (row: T) => void;
  /**
   * The row whose thing is open beside the table, such as the record in a side
   * peek. Tinted, and announced as the current one.
   */
  selectedRowKey?: string | null;

  /** The trailing cell. `TableActionsMenu` is the usual thing to put in it. */
  actions?: (row: T) => ReactNode;

  /**
   * Existing rows stay on screen and dim while a reload runs — a table that
   * empties itself between pages loses the reader's place.
   */
  loading?: boolean;
  emptyState?: ReactNode;
  error?: ReactNode;
  /** Banded rows. Off by default: the row borders already separate them. */
  zebra?: boolean;
  /**
   * A floor under the table's width, so a wide one scrolls sideways inside its
   * own box instead of squeezing every column into two words per line. Both
   * sizes are the layout widths the design already names — `md` is the mobile
   * breakpoint, `lg` the agenda one.
   *
   * The scroll box also becomes a tab stop, so the columns past the edge can
   * be reached with the arrow keys rather than only by dragging.
   */
  minWidth?: DataTableMinWidth;
  /** Classes for the scroll container — a `max-h-*` makes the header stick. */
  className?: string;
}

/**
 * A list of records, dense enough to compare and wide enough to read.
 *
 * Everything that changes what is on screen — the sort, the page, the query —
 * is controlled: the table renders state and reports intent, and whether that
 * means a fetch or an `Array.sort` is decided where the rows come from. The
 * `useClient*` helpers cover the second case.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  label,
  sort,
  onSortChange,
  pagination,
  onPageChange,
  search,
  toolbar,
  rowHref,
  onNavigate,
  onRowClick,
  selectedRowKey,
  actions,
  loading = false,
  emptyState,
  error,
  zebra = false,
  minWidth,
  className,
}: DataTableProps<T>) {
  const reloading = loading && rows.length > 0;
  const showEmpty = !loading && !error && rows.length === 0;

  return (
    <div data-slot='data-table' className='flex flex-col gap-3'>
      <TableToolbar search={search} toolbar={toolbar} />

      <div className='relative rounded-md border border-line bg-surface'>
        <TableScrollBox scrolls={Boolean(minWidth)} className={className}>
          <table
            aria-label={label}
            aria-busy={loading || undefined}
            className={cn(
              'w-full border-collapse text-sm',
              minWidth && minWidthClass[minWidth],
            )}
          >
            <TableHead
              columns={columns}
              sort={sort}
              onSortChange={onSortChange}
              hasActions={Boolean(actions)}
            />

            <tbody className={cn(reloading && 'opacity-50 transition-opacity')}>
              {loading && rows.length === 0 && (
                <TableSkeletonRows columnCount={columns.length + (actions ? 1 : 0)} />
              )}

              {rows.map((row) => {
                const key = rowKey(row);

                return (
                  <TableRow
                    key={key}
                    row={row}
                    columns={columns}
                    rowHref={rowHref}
                    onNavigate={onNavigate}
                    onRowClick={onRowClick}
                    selected={key === selectedRowKey}
                    actions={actions}
                    zebra={zebra}
                  />
                );
              })}
            </tbody>
          </table>
        </TableScrollBox>

        {/*
         * Outside the table, so a message on a table wider than the screen sits
         * in the part you can see rather than across the whole scroll width.
         */}
        {error !== undefined && error !== null && (
          <div className='border-t border-line px-3.5 py-6 text-center text-sm font-semibold text-danger'>
            {error}
          </div>
        )}

        {showEmpty && (
          <div className='border-t border-line px-3.5 py-2'>
            {emptyState ?? <EmptyState title='Nothing here yet' />}
          </div>
        )}

        {reloading && (
          <span
            data-slot='data-table-loading'
            role='status'
            className='pointer-events-none absolute inset-0 flex items-start justify-center pt-16 text-ink-muted'
          >
            <Spinner className='size-5' />
            <span className='sr-only'>Loading</span>
          </span>
        )}

        {pagination && <TablePagination {...pagination} onPageChange={onPageChange} />}
      </div>
    </div>
  );
}
